import React from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {useAccommodationTopOffers} from 'components/Accommodation/Offers/useAccommodationTopOffers'
import {useIsraeliVatEligibility} from 'hooks/useIsraeliVatEligibility'
import {
  RoomOfferVioTagDetails,
  useRoomOfferVioTagDetails
} from 'hooks/useRoomOfferVioTag'
import {getAllTaxesIncluded} from 'modules/meta/selectors'
import {
  getAnchorPriceNightlyPerRoom,
  getNumberOfRooms,
  getSearchParameters,
  getShowTotalPrices
} from 'modules/sapiSearch/selectors'
import {getAnchorPriceTotal} from 'modules/sapiSearch/utils'

import {Box} from '@vio/atlas/helpers/Box'
import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {defaultCurrencyCode} from '@vio/core/src/localization/business/currency'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import {Offer} from '@vio/core/src/offer/types/offer'
import {getRoomOfferPrices} from '@vio/core/src/price/business/roomOfferPrices'
import {getLengthOfStay} from '@vio/core/src/utils/date'
import {getRoundingStrategy, RoundingStrategy} from '@vio/core/src/utils/number'
import {OfferAlertBannerRedesigned} from '@vio/shared/src/accommodation/OfferAlertBannerRedesigned'
import {OfferAlertVariant} from '@vio/shared/src/accommodation/OfferAlertBannerRedesigned/constants'
import {Price} from '@vio/shared/src/search/Price'
import {usePointsPriceDisplay} from '@vio/shared/src/whiteLabel/hooks/usePointsPriceDisplay'
import {loyaltyMessages} from '@vio/shared/src/whiteLabel/loyaltyMessages'
import {
  PointsPrice,
  usePointsPriceValues
} from '@vio/shared/src/whiteLabel/PointsPrice'

import {RoomOfferAccessFlowLabel} from '../Rooms/RoomOffer/RoomOfferAccessFlowLabel'
import {
  priceSuffixMessages,
  RoomOfferPriceDataId
} from '../Rooms/RoomOfferPrice/constants'
import {RoomOfferVioTag} from '../Rooms/RoomOfferVioTag'
import {useRoomOfferPriceChangeAlert} from './useRoomOfferPriceChangeAlert'

interface Props {
  hotelId: string
  offer: Offer
  isPrivate?: boolean
  isOfferCheapest?: boolean
  roundingStrategy?: RoundingStrategy
  isOfferSoldOut?: boolean
  isSplitBooking?: boolean
  roomOfferVioTagDetails?: RoomOfferVioTagDetails
  isCompact?: boolean
}

interface RoomOfferPriceBreakdownProps
  extends Pick<Props, 'hotelId' | 'roundingStrategy' | 'isOfferSoldOut'> {
  isSplitBooking?: boolean
  detailsPriceValue: number
  currencyCode: string
  detailsPriceDataId: RoomOfferPriceDataId
  numberOfRooms: number
  showTotalPrices: boolean
  isCompact?: boolean
}

const PriceSuffix = styled(Text)<{isOfferSoldOut?: boolean}>`
  white-space: nowrap;
  color: ${({isOfferSoldOut}) =>
    isOfferSoldOut
      ? cssTheme.colors.content.neutral.disabled
      : cssTheme.colors.content.neutral.primary};
`

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  white-space: pre-wrap;
`

const TextWrapper = styled(Text)<{isOfferSoldOut?: boolean}>`
  color: ${({isOfferSoldOut}) =>
    isOfferSoldOut
      ? cssTheme.colors.content.neutral.disabled
      : cssTheme.colors.content.neutral.primary};
`

export const RoomOfferPriceBreakdown = ({
  isOfferSoldOut,
  isSplitBooking,
  hotelId,
  detailsPriceValue,
  currencyCode,
  roundingStrategy,
  detailsPriceDataId,
  numberOfRooms,
  showTotalPrices,
  isCompact = false
}: RoomOfferPriceBreakdownProps) => {
  const {formatMessage} = useIntl()
  const {hasAdditionalCharges} = useIsraeliVatEligibility(hotelId)
  const {checkIn, checkOut} = useSelector(getSearchParameters) || {}
  const lengthOfStay =
    checkIn && checkOut ? getLengthOfStay(checkIn, checkOut) : 0

  const includeTaxes = useSelector(getAllTaxesIncluded)
  const points = usePointsPriceDisplay()
  const pointsPriceValues = usePointsPriceValues()

  if (isCompact) return null

  const soldOutColor = isOfferSoldOut
    ? cssTheme.colors.content.neutral.disabled
    : null
  return (
    <>
      <Wrapper>
        {points.isOn && !showTotalPrices ? (
          <TextWrapper isOfferSoldOut={isOfferSoldOut} variant="bodyS">
            <FormattedMessageWrapper
              {...loyaltyMessages.pointsPriceTotal}
              values={pointsPriceValues({
                ...points.splitParts(detailsPriceValue),
                currencyCode
              })}
            />
          </TextWrapper>
        ) : (
          <>
            {showTotalPrices ? null : (
              <>
                {/** Total price for the nightly offer price */}
                <Text variant="bodyS">
                  <Price
                    currency={currencyCode}
                    value={detailsPriceValue}
                    roundingStrategy={roundingStrategy}
                    noStyle
                    dataId={detailsPriceDataId}
                    style={{
                      color: soldOutColor
                    }}
                  />
                </Text>
                {<>&nbsp;</>}
              </>
            )}
            {/** Total price label like total, 1 room, 2 nights, etc. */}
            <TextWrapper isOfferSoldOut={isOfferSoldOut} variant="bodyS">
              {formatMessage(priceSuffixMessages.totalV2)}
              {<>&nbsp;</>}
              {!isSplitBooking &&
                formatMessage(priceSuffixMessages.numberOfRoomsMessage, {
                  count: numberOfRooms
                })}
              {!isSplitBooking && <>,&nbsp;</>}
              {formatMessage(priceSuffixMessages.numberOfNightsMessage, {
                count: lengthOfStay
              })}
            </TextWrapper>
          </>
        )}
      </Wrapper>
      <Box display="flex" justifyContent="flex-end">
        {/** Additional charges message like Additional charges may apply or incl. taxes & fees */}
        <TextWrapper
          isOfferSoldOut={isOfferSoldOut}
          variant="bodyS"
          dataId="includingTaxes"
        >
          {hasAdditionalCharges
            ? formatMessage(priceSuffixMessages.additionalCharges)
            : includeTaxes || showTotalPrices
              ? formatMessage(priceSuffixMessages.withTaxesAndFees)
              : formatMessage(priceSuffixMessages.includingTaxes)}
        </TextWrapper>
      </Box>
    </>
  )
}

export const RoomOfferCardPriceInfo = ({
  hotelId,
  offer,
  isPrivate = false,
  roundingStrategy = getRoundingStrategy(),
  isOfferSoldOut = false,
  isSplitBooking = false,
  isOfferCheapest = false,
  isCompact = false
}: Props) => {
  const {formatMessage} = useIntl()
  const {isMobile} = useDeviceLayout()
  const points = usePointsPriceDisplay()
  const {offerEntity} = useAccommodationTopOffers({
    hotelId
  })
  const {id} = offer

  const roomOfferVioTagDetails = useRoomOfferVioTagDetails({
    offer
  })
  const numberOfRooms = useSelector(state => getNumberOfRooms(state, hotelId))

  const {isPriceChangeBannerVisible, priceDiff} = useRoomOfferPriceChangeAlert({
    offer,
    isOfferSoldOut,
    isCompact
  })

  const anchorPrice = useSelector(state =>
    getAnchorPriceNightlyPerRoom(state, offerEntity)
  )
  const anchorPriceTotal = getAnchorPriceTotal(offerEntity)

  const showTotalPrices = useSelector(getShowTotalPrices)
  const prices = getRoomOfferPrices(offer.prices, {
    numberOfRooms,
    anchorNightly: anchorPrice,
    anchorTotal: anchorPriceTotal
  })
  if (!prices) return null

  const {currencyCode} = prices
  const mainPrice = showTotalPrices ? prices.total : prices.nightly
  const detailsPrice = showTotalPrices ? prices.nightly : prices.total

  const soldOutColor = isOfferSoldOut
    ? cssTheme.colors.content.neutral.disabled
    : null

  const mainPriceDataId = showTotalPrices
    ? RoomOfferPriceDataId.Total
    : RoomOfferPriceDataId.PerNight

  const detailsPriceDataId = showTotalPrices
    ? RoomOfferPriceDataId.PerNight
    : RoomOfferPriceDataId.Total

  const isVioTagVisible =
    isOfferCheapest && roomOfferVioTagDetails.isVioTagVisible && !isOfferSoldOut

  return (
    <Box
      display="flex"
      flexDirection="column"
      flexWrap="wrap"
      alignItems={isMobile ? 'flex-end' : 'flex-start'}
      justifyContent="center"
    >
      {/** Offer alert banner */}
      {isPriceChangeBannerVisible && (
        <Box paddingBottom="s250" alignSelf="flex-start">
          <OfferAlertBannerRedesigned
            totalPriceDiff={priceDiff}
            currency={currencyCode || defaultCurrencyCode}
            variant={
              priceDiff > 0
                ? OfferAlertVariant.PriceIncreaseV2
                : OfferAlertVariant.PriceDropV2
            }
          />
        </Box>
      )}

      {/** Only on Vio tag */}
      {isVioTagVisible && !isCompact && (
        <RoomOfferVioTag
          roomOfferVioTagDetails={roomOfferVioTagDetails}
          wrapper={(children: React.ReactNode) => (
            <Box paddingBottom="s200">{children}</Box>
          )}
        />
      )}

      {/** Access flow label like Sign in to book, Book on Vio app, etc. */}
      {!isOfferSoldOut && !isCompact && (
        <RoomOfferAccessFlowLabel offerId={id} />
      )}

      {points.isOn ? (
        <PointsPrice
          {...points.splitParts(mainPrice.price)}
          currencyCode={currencyCode}
          showMaxPointsCaption={!isCompact}
          isMuted={isOfferSoldOut}
          dataId={mainPriceDataId}
          suffix={
            !showTotalPrices && (
              <PriceSuffix
                tag="span"
                variant="bodyS"
                isOfferSoldOut={isOfferSoldOut}
              >
                {formatMessage(priceSuffixMessages.perNight)}
              </PriceSuffix>
            )
          }
        />
      ) : (
        <Box
          display="flex"
          alignItems="baseline"
          justifyContent="flex-start"
          columnGap="s200"
          flexWrap="wrap"
        >
          {/** Anchor price */}
          {isVioTagVisible && mainPrice.showAnchor && (
            <Price
              isAnchor
              currency={currencyCode}
              value={mainPrice.anchor}
              roundingStrategy={roundingStrategy}
              style={{
                color: soldOutColor
              }}
            />
          )}
          <Box
            display="flex"
            flexWrap="wrap"
            columnGap="s200"
            alignItems="baseline"
            justifyContent="flex-start"
          >
            {/** Main price */}
            <Price
              isPrivate={isPrivate || isVioTagVisible}
              currency={currencyCode}
              value={mainPrice.price}
              roundingStrategy={roundingStrategy}
              size="lg"
              dataId={mainPriceDataId}
              style={{
                color: soldOutColor
              }}
            />
            {/** Price suffix like / night */}
            <PriceSuffix variant="bodyS" isOfferSoldOut={isOfferSoldOut}>
              {showTotalPrices
                ? null
                : formatMessage(priceSuffixMessages.perNight)}
            </PriceSuffix>
          </Box>
        </Box>
      )}

      {/** Price breakdown like 1 room, 2 nights, etc. */}
      <RoomOfferPriceBreakdown
        isSplitBooking={isSplitBooking}
        hotelId={hotelId}
        detailsPriceValue={detailsPrice.price}
        currencyCode={currencyCode}
        roundingStrategy={roundingStrategy}
        detailsPriceDataId={detailsPriceDataId}
        numberOfRooms={numberOfRooms}
        showTotalPrices={showTotalPrices}
        isOfferSoldOut={isOfferSoldOut}
        isCompact={isCompact}
      />
    </Box>
  )
}
